import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { Category, CreateDiscountRuleLineRequest, DiscountTargetType, Product } from '@bazaar/shared';
import { api, ApiError } from '../../lib/api.js';
import { useToast } from '../../context/ToastContext.js';
import { summarizeDiscountRuleDraft } from '../../lib/discountSummary.js';
import { BigButton } from '../../components/BigButton.js';
import { MoneyInput } from '../../components/MoneyInput.js';
import { QtyStepper } from '../../components/QtyStepper.js';
import { SegmentedControl } from '../../components/SegmentedControl.js';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { Spinner } from '../../components/Spinner.js';
import { Page, PageHeader, Field } from '../../components/layout.js';

// Date-only pickers, converted at the KL-fixed UTC+8 offset the rest of the
// app uses for calendar-day math (see api/src/lib/time.ts) - this is just
// the web form's own convenience conversion, not a second source of truth.
const KL_OFFSET_MS = 8 * 60 * 60 * 1000;

function isoToKlDateString(iso: string): string {
  return new Date(new Date(iso).getTime() + KL_OFFSET_MS).toISOString().slice(0, 10);
}

function addDaysToDateString(dateStr: string, days: number): string {
  const d = new Date(`${dateStr}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function klDateStringToUtcIso(dateStr: string): string {
  return new Date(`${dateStr}T00:00:00+08:00`).toISOString();
}

let lineKeyCounter = 0;
function nextLineKey(): string {
  lineKeyCounter += 1;
  return `line-${lineKeyCounter}`;
}

interface LineDraft {
  key: string;
  targetType: DiscountTargetType;
  productId: number | null;
  categoryId: number | null;
  minQty: number;
  search: string;
}

function emptyLine(): LineDraft {
  return { key: nextLineKey(), targetType: 'product', productId: null, categoryId: null, minQty: 1, search: '' };
}

const KIND_OPTIONS = [
  { value: 'percent' as const, label: 'Percent' },
  { value: 'amount' as const, label: 'Amount' },
];

const TARGET_OPTIONS = [
  { value: 'category' as const, label: 'Category' },
  { value: 'product' as const, label: 'Product' },
];

export function DiscountRuleFormPage() {
  const { id } = useParams<{ id: string }>();
  const isEdit = id !== undefined;
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [loading, setLoading] = useState(isEdit);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);

  const [name, setName] = useState('');
  const [kind, setKind] = useState<'percent' | 'amount'>('percent');
  const [percent, setPercent] = useState(10);
  const [amountCents, setAmountCents] = useState(0);
  const [lines, setLines] = useState<LineDraft[]>([emptyLine()]);
  const [isActive, setIsActive] = useState(true);
  const [startsAtDate, setStartsAtDate] = useState('');
  const [endsAtDate, setEndsAtDate] = useState('');

  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    try {
      const [productsRes, categoriesRes] = await Promise.all([api.listProducts({}), api.listCategories()]);
      setProducts(productsRes.products);
      setCategories(categoriesRes.categories);

      if (isEdit) {
        const rules = await api.listDiscountRules();
        const rule = rules.rules.find((r) => r.id === Number(id));
        if (!rule) {
          setLoadError('Discount rule not found');
          return;
        }
        setName(rule.name);
        setKind(rule.kind);
        if (rule.kind === 'percent') setPercent((rule.percentBp ?? 0) / 100);
        else setAmountCents(rule.amountCents ?? 0);
        setLines(
          rule.lines.length > 0
            ? rule.lines.map((l) => ({
                key: nextLineKey(),
                targetType: l.targetType,
                productId: l.productId,
                categoryId: l.categoryId,
                minQty: l.minQty,
                search: '',
              }))
            : [emptyLine()],
        );
        setIsActive(rule.isActive);
        setStartsAtDate(rule.startsAt ? isoToKlDateString(rule.startsAt) : '');
        setEndsAtDate(rule.endsAt ? addDaysToDateString(isoToKlDateString(rule.endsAt), -1) : '');
      }
    } catch (err) {
      setLoadError(err instanceof ApiError ? err.message : 'Could not load this rule');
    } finally {
      setLoading(false);
    }
  }, [id, isEdit]);

  useEffect(() => {
    void load();
  }, [load]);

  function updateLine(key: string, patch: Partial<LineDraft>) {
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  }

  function removeLine(key: string) {
    setLines((prev) => (prev.length > 1 ? prev.filter((l) => l.key !== key) : prev));
  }

  function addLine() {
    setLines((prev) => [...prev, emptyLine()]);
  }

  function labelFor(line: LineDraft): string {
    if (line.targetType === 'product') {
      return products.find((p) => p.id === line.productId)?.name ?? '';
    }
    return categories.find((c) => c.id === line.categoryId)?.name ?? '';
  }

  const previewSentence = summarizeDiscountRuleDraft({
    kind,
    percent,
    amountCents,
    lines: lines
      .filter((l) => (l.targetType === 'product' ? l.productId !== null : l.categoryId !== null))
      .map((l) => ({ minQty: l.minQty, label: labelFor(l) })),
  });

  const linesValid = lines.every((l) => (l.targetType === 'product' ? l.productId !== null : l.categoryId !== null));
  const canSave =
    name.trim().length > 0 &&
    linesValid &&
    (kind === 'percent' ? percent >= 1 && percent <= 100 : amountCents > 0) &&
    (!startsAtDate || !endsAtDate || endsAtDate >= startsAtDate) &&
    !saving;

  async function handleSave() {
    if (!canSave) return;
    setSaving(true);
    setFormError(null);
    try {
      const requestLines: CreateDiscountRuleLineRequest[] = lines.map((l) => ({
        targetType: l.targetType,
        productId: l.targetType === 'product' ? l.productId : null,
        categoryId: l.targetType === 'category' ? l.categoryId : null,
        minQty: l.minQty,
      }));
      const payload = {
        name: name.trim(),
        kind,
        ...(kind === 'percent' ? { percent } : { amountCents }),
        lines: requestLines,
        isActive,
        startsAt: startsAtDate ? klDateStringToUtcIso(startsAtDate) : null,
        endsAt: endsAtDate ? klDateStringToUtcIso(addDaysToDateString(endsAtDate, 1)) : null,
      };

      if (isEdit) {
        await api.updateDiscountRule(Number(id), payload);
        showToast('Discount rule updated', 'success');
      } else {
        await api.createDiscountRule(payload);
        showToast('Discount rule created', 'success');
      }
      navigate('/admin/discounts');
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Could not save this rule';
      setFormError(message);
      showToast(message, 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!isEdit) return;
    setDeleting(true);
    try {
      await api.deleteDiscountRule(Number(id));
      showToast('Discount rule deleted', 'success');
      navigate('/admin/discounts');
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : 'Could not delete this rule', 'error');
    } finally {
      setDeleting(false);
      setDeleteOpen(false);
    }
  }

  if (loading) return <Spinner label="Loading discount rule" />;
  if (loadError) return <p className="font-body text-base text-danger p-4">{loadError}</p>;

  return (
    <Page>
      <PageHeader title={isEdit ? 'Edit discount rule' : 'New discount rule'} />

      <div className="card card-stack">
        <Field label="Name" htmlFor="rule-name" help='Shown to staff, e.g. "Festive keychains".'>
          <input
            id="rule-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={80}
            className="input text-lg font-heading"
          />
        </Field>

        <div>
          <p className="field-label mb-2">Type</p>
          <SegmentedControl options={KIND_OPTIONS} value={kind} onChange={setKind} ariaLabel="Discount type" />
        </div>

        {kind === 'percent' ? (
          <Field label="Percent off" htmlFor="rule-percent">
            <input
              id="rule-percent"
              type="number"
              inputMode="numeric"
              min={1}
              max={100}
              value={percent}
              onChange={(e) => setPercent(Math.max(1, Math.min(100, Number(e.target.value) || 1)))}
              className="input text-lg font-heading"
            />
          </Field>
        ) : (
          <MoneyInput id="rule-amount" label="Amount off" valueCents={amountCents} onChangeCents={(c) => setAmountCents(c ?? 0)} />
        )}

        <div className="section-stack">
          <p className="field-label">Conditions (all must be met)</p>
          <ul className="list-stack">
            {lines.map((line) => {
              const pool = line.targetType === 'product' ? products : categories;
              const needle = line.search.trim().toLowerCase();
              const filtered = pool.filter((p) => p.name.toLowerCase().includes(needle));
              const selectedId = line.targetType === 'product' ? line.productId : line.categoryId;

              return (
                <li key={line.key} className="card-row flex flex-col gap-3">
                  <SegmentedControl
                    options={TARGET_OPTIONS}
                    value={line.targetType}
                    onChange={(targetType) => updateLine(line.key, { targetType, productId: null, categoryId: null, search: '' })}
                    ariaLabel="Condition target"
                  />

                  <input
                    type="text"
                    placeholder={`Search ${line.targetType === 'product' ? 'products' : 'categories'}…`}
                    value={line.search}
                    onChange={(e) => updateLine(line.key, { search: e.target.value })}
                    className="input"
                  />

                  <select
                    aria-label={`Select a ${line.targetType}`}
                    value={selectedId ?? ''}
                    onChange={(e) => {
                      const value = e.target.value === '' ? null : Number(e.target.value);
                      updateLine(
                        line.key,
                        line.targetType === 'product' ? { productId: value } : { categoryId: value },
                      );
                    }}
                    className="input"
                  >
                    <option value="">Select a {line.targetType}…</option>
                    {filtered.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </select>

                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <span className="field-label">Qty</span>
                      <QtyStepper
                        qty={line.minQty}
                        max={10_000}
                        onIncrement={() => updateLine(line.key, { minQty: line.minQty + 1 })}
                        onDecrement={() => updateLine(line.key, { minQty: Math.max(1, line.minQty - 1) })}
                        label="condition quantity"
                      />
                    </div>
                    <button
                      type="button"
                      disabled={lines.length <= 1}
                      onClick={() => removeLine(line.key)}
                      className="min-h-12 px-3 rounded-lg font-heading font-semibold text-base text-danger disabled:opacity-30"
                    >
                      Remove
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
          <BigButton variant="outline" onClick={addLine}>
            Add condition
          </BigButton>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Starts (optional)" htmlFor="rule-starts">
            <input
              id="rule-starts"
              type="date"
              value={startsAtDate}
              onChange={(e) => setStartsAtDate(e.target.value)}
              className="input"
            />
          </Field>
          <Field label="Ends (optional)" htmlFor="rule-ends">
            <input id="rule-ends" type="date" value={endsAtDate} onChange={(e) => setEndsAtDate(e.target.value)} className="input" />
          </Field>
        </div>

        <label className="flex items-center gap-3 font-body font-semibold text-base text-text min-h-12">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            className="h-6 w-6 min-h-0 accent-teal shrink-0"
          />
          Active
        </label>

        <div className="rounded-lg bg-aqua px-4 py-3">
          <p className="font-body text-sm text-teal-dark">Preview</p>
          <p className="font-heading font-semibold text-base text-teal-dark">{previewSentence}</p>
        </div>

        {formError && (
          <p role="alert" className="font-body text-sm text-danger">
            {formError}
          </p>
        )}

        <div className="flex gap-3 justify-between">
          {isEdit ? (
            <BigButton variant="outline-danger" onClick={() => setDeleteOpen(true)}>
              Delete
            </BigButton>
          ) : (
            <span />
          )}
          <div className="flex gap-3">
            <BigButton variant="outline" onClick={() => navigate('/admin/discounts')}>
              Cancel
            </BigButton>
            <BigButton disabled={!canSave} onClick={() => void handleSave()}>
              {saving ? 'Saving…' : 'Save'}
            </BigButton>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={deleteOpen}
        title={`Delete ${name || 'this rule'}?`}
        description="Past sales keep their own record of the discount - this only stops it applying to future sales."
        confirmLabel={deleting ? 'Deleting…' : 'Delete'}
        variant="danger"
        onConfirm={() => void handleDelete()}
        onCancel={() => setDeleteOpen(false)}
      />
    </Page>
  );
}
